import React, { useState } from 'react';
import Card from '~/src/components/Card';
import InlineNotification from '~/src/components/Notification/InlineNotification';
import ModalOverlay from '~/src/components/Modal/Overlay';
import PlanChangeOptionsCard from '~/src/pages/Settings/subpages/SettingsSubscription/SettingsCbsSubscription/PlanUpgrade/PlanChangeOptionsCard';
import { PlanChangePreviewModal } from '~/src/pages/Settings/subpages/SettingsSubscription/SettingsCbsSubscription/PlanUpgrade/PlanChangePreviewModal';
import { usePlanUpgradeOptions } from '~/src/entities/subscription/hooks/usePlanUpgradeOptions';
import { PlanUpgradeOption } from '~/src/entities/subscription/types';
import useUser from '~/src/hooks/useUser';
import cbsSubscriptionsService from '~/src/services/api/cbsSubscriptionsService';

interface PlanChangeCardProps {
  subscription: any;
}

const PlanChangeCard = ({ subscription }: PlanChangeCardProps) => {
  const { data, isLoading, error: planOptionsError } = usePlanUpgradeOptions();
  const { currentOrganization } = useUser();

  const [previewData, setPreviewData] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<PlanUpgradeOption | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const handlePlanChange = async (plan: PlanUpgradeOption) => {
    setSelectedPlan(plan);
    setError(null);
    setPreviewData(null);

    try {
      const orgFprint = currentOrganization?.fprint;
      if (!orgFprint) {
        throw new Error('Organization fingerprint not found');
      }

      const result = await cbsSubscriptionsService.getPlanUpgradePreview(
        orgFprint,
        plan.id.toString(),
      );

      setPreviewData(result);
      setShowModal(true);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load plan preview');
    }
  };

  const handleConfirmChange = async () => {
    if (!selectedPlan || !currentOrganization?.fprint) {
      setError('Missing required information for plan upgrade');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await cbsSubscriptionsService.confirmPlanUpgrade(
        currentOrganization.fprint,
        selectedPlan.id.toString(),
      );

      setShowModal(false);
      setPreviewData(null);
      setSuccessMessage(
        `Successfully upgraded to ${selectedPlan.display_name}!`,
      );

      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (err: any) {
      setShowModal(false);
      setPreviewData(null);
      setError(err?.message ?? 'Failed to confirm plan upgrade');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancelPreview = () => {
    setShowModal(false);
    setPreviewData(null);
    setSelectedPlan(null);
    setError(null);
  };

  const availablePlans = data?.data || [];

  if (isLoading || planOptionsError || availablePlans.length === 0) {
    return null;
  }

  return (
    <>
      <Card>
        <div className="text-lg font-bold mb-2">Change Plan</div>

        <div className="space-y-3">
          {availablePlans.map((plan: PlanUpgradeOption) => (
            <PlanChangeOptionsCard
              key={plan.id}
              plan={plan}
              onPreview={handlePlanChange}
            />
          ))}
        </div>

        {(error || successMessage) && (
          <div className="mt-4">
            {error && (
              <InlineNotification
                type="error"
                title="Upgrade Failed"
                message={error}
              />
            )}
            {successMessage && (
              <InlineNotification
                type="success"
                title="Success"
                message={successMessage}
              />
            )}
          </div>
        )}
      </Card>

      <ModalOverlay show={showModal} onCancel={handleCancelPreview}>
        {previewData && selectedPlan && (
          <PlanChangePreviewModal
            previewData={previewData}
            currentPlanName={subscription?.plan?.name || 'Current Plan'}
            newPlanName={selectedPlan.display_name}
            onCancel={handleCancelPreview}
            onConfirm={handleConfirmChange}
            isProcessing={isProcessing}
          />
        )}
      </ModalOverlay>
    </>
  );
};

export default PlanChangeCard;
